import {DefaultFoundationTheme, Integer} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {Nullish} from 'shared/web';

export type StyledEditorProps = {
  $isDisabled?: boolean;
  $isResizable?: boolean;
  $isScrollable?: boolean;
  $rows?: Integer;
  $minRows?: Integer | Nullish;
  $maxRows?: Integer | Nullish;
};

export const EditorContainer = styled.div`
  position: relative;
`;

const richTextContentStyles = css`
  padding: ${({theme}) => `${theme.getSize(2)} ${theme.getSize(2)}`};
  color: ${({theme}) => theme.colors.text.primary};
  font-size: ${({theme}) => theme.fontSizes.text.small};
  line-height: ${({theme}) => theme.lineHeights.text.small};
  font-weight: ${({theme}) => theme.fontWeights.text.default};

  p,
  h1,
  h2,
  h3,
  blockquote,
  ul,
  ol {
    margin: 0;
  }

  h1 {
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[1]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[1]};
    font-weight: ${({theme}) => theme.fontWeights.bold};
  }

  h2 {
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[2]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[2]};
    font-weight: ${({theme}) => theme.fontWeights.bold};
  }

  h3 {
    font-size: ${({theme}) => theme.fontSizes.heading.desktop[3]};
    line-height: ${({theme}) => theme.lineHeights.heading.desktop[3]};
    font-weight: ${({theme}) => theme.fontWeights.medium};
  }

  ul,
  ol {
    padding-left: ${({theme}) => theme.getSize(5)};
  }

  blockquote {
    padding-left: ${({theme}) => theme.getSize(2)};
    border-left: 2px solid ${({theme}) => theme.colors.palettes.neutral[50][100]};
    font-style: italic;
    color: ${({theme}) => theme.colors.text.secondary};
  }
`;

export const StyledEditor = styled.div<StyledEditorProps>`
  min-height: ${({theme, $rows, $minRows}) => getEditorHeight(theme, $minRows ?? $rows ?? 6)};
  max-height: ${({theme, $maxRows}) => ($maxRows ? getEditorHeight(theme, $maxRows) : 'none')};
  overflow-y: ${({$isScrollable}) => ($isScrollable ? 'auto' : 'hidden')};
  resize: ${({$isResizable}) => ($isResizable ? 'vertical' : 'none')};
  outline: 0;
  ${richTextContentStyles}
  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      cursor: not-allowed;
      user-select: none;
    `}

  &:empty::before {
    content: '';
  }
`;

export const RichTextContent = styled.div`
  ${richTextContentStyles}
`;

export const PlaceholderText = styled.div`
  position: absolute;
  top: ${({theme}) => theme.getSize(2)};
  left: ${({theme}) => theme.getSize(2)};
  color: ${({theme}) => theme.colors.text.tertiary};
  pointer-events: none;
`;

function getEditorHeight(theme: DefaultFoundationTheme, rows: Integer) {
  return `calc(${rows} * ${theme.lineHeights.text.small} + ${theme.getSize(4)})`;
}
